import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle, Save, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Checkbox } from "@cms/ui/components/checkbox";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Textarea } from "@cms/ui/components/textarea";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import { PageHeader } from "@cms/ui/components/page-header";
import { ImageField } from "../components/image-field";
import { EventRecurrenceCard } from "../components/event-recurrence-card";
import { EventBookingCard } from "../components/event-booking-card";
import {
  descriptionToDoc,
  docToDescription,
  fetchEvent,
  parseEventRecurrence,
  regenerateEventsFile,
  saveEvent,
  type EventRecurrence,
  type EventSavePayload,
} from "../lib/events";
import type { ClientOutletContext } from "../lib/outlet-context";

interface EventDraft {
  title: string;
  featured: boolean;
  imageKey: string | null;
  imageUrl: string | null;
  imageAlt: string;
  date: string;
  endDate: string;
  allDay: boolean;
  startTime: string;
  endTime: string;
  location: string;
  description: string;
  linkUrl: string;
  linkLabel: string;
  recurrence: EventRecurrence | null;
  published: boolean;
}

export function EventEditorPage() {
  const { api, setBreadcrumbs, activeModules } = useOutletContext<ClientOutletContext>();
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<EventDraft | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [fileWarning, setFileWarning] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!api || !id) return;
    fetchEvent(api, id)
      .then((event) =>
        setDraft({
          title: event.title,
          featured: event.featured === 1,
          imageKey: event.image_key,
          imageUrl: null,
          imageAlt: event.image_alt ?? "",
          date: event.date,
          endDate: event.end_date ?? "",
          allDay: event.all_day === 1,
          startTime: event.start_time ?? "",
          endTime: event.end_time ?? "",
          location: event.location ?? "",
          description: docToDescription(event.description),
          linkUrl: event.link_url ?? "",
          linkLabel: event.link_label ?? "",
          recurrence: parseEventRecurrence(event.recurrence),
          published: event.published === 1,
        })
      )
      .catch((err: Error) => setError(err.message));
  }, [api, id]);

  const loaded = draft !== null;
  const eventTitle = draft?.title ?? "";

  useEffect(() => {
    if (!setBreadcrumbs) return undefined;
    setBreadcrumbs(loaded ? [
      { label: "Events", href: "/events" },
      { label: eventTitle || "Event" },
    ] : null);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs, loaded, eventTitle]);

  const patch = (changes: Partial<EventDraft>) => {
    setDraft((prev) => (prev ? { ...prev, ...changes } : prev));
    setSavedAt(null);
  };

  const save = async () => {
    if (!api || !draft) return;
    if (!draft.title.trim() || !draft.date) {
      setSaveError("A title and date are required.");
      return;
    }
    if (draft.endDate && draft.endDate < draft.date) {
      setSaveError("The end date can't be before the start date.");
      return;
    }
    setSaving(true);
    setSaveError("");
    setFileWarning("");
    const payload: EventSavePayload = {
      title: draft.title.trim(),
      featured: draft.featured ? 1 : 0,
      image_key: draft.imageKey,
      image_alt: draft.imageAlt.trim() || null,
      date: draft.date,
      end_date: draft.endDate || null,
      all_day: draft.allDay ? 1 : 0,
      start_time: draft.allDay ? null : draft.startTime || null,
      end_time: draft.allDay ? null : draft.endTime || null,
      location: draft.location.trim() || null,
      description: descriptionToDoc(draft.description),
      link_url: draft.linkUrl.trim() || null,
      link_label: draft.linkLabel.trim() || null,
      recurrence: draft.recurrence,
      published: draft.published ? 1 : 0,
    };
    try {
      const result = await saveEvent(api, id, payload);
      setSavedAt(Date.now());
      if (!result.events_file.updated && draft.published) {
        setFileWarning(result.events_file.reason ?? "The live events file couldn't be updated.");
      }
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const retryFile = async () => {
    if (!api) return;
    setFileWarning("");
    try {
      await regenerateEventsFile(api);
    } catch (err) {
      setFileWarning((err as Error).message);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Couldn't load this event</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!draft) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={draft.title || "Event"}
        description="Changes to a live event appear on your site as soon as you save."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate("/events")}>
              <ArrowLeft className="h-4 w-4" />
              All events
            </Button>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.published}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ published: e.target.checked })}
              />
              Live
            </label>
            <Button onClick={() => { void save(); }} size="sm" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {savedAt ? "Saved" : "Save"}
            </Button>
          </>
        }
      />

      {saveError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't save</AlertTitle>
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      {fileWarning && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Saved, but the live events file wasn't updated</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            <span>{fileWarning}</span>
            <Button variant="outline" size="sm" onClick={() => { void retryFile(); }}>
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="event-title">Title</Label>
                <Input
                  id="event-title"
                  value={draft.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ title: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="event-date">Date</Label>
                  <Input
                    id="event-date"
                    type="date"
                    value={draft.date}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="event-end-date">End date (multi-day)</Label>
                  <Input
                    id="event-end-date"
                    type="date"
                    min={draft.date}
                    value={draft.endDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ endDate: e.target.value })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.allDay}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ allDay: e.target.checked })}
                />
                All day
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="event-start-time">Start time</Label>
                  <Input
                    id="event-start-time"
                    type="time"
                    value={draft.startTime}
                    disabled={draft.allDay}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ startTime: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="event-end-time">End time</Label>
                  <Input
                    id="event-end-time"
                    type="time"
                    value={draft.endTime}
                    disabled={draft.allDay}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ endTime: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="event-location">Location</Label>
                <Input
                  id="event-location"
                  value={draft.location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ location: e.target.value })}
                  placeholder="e.g. The Barrow Café, or a video call link"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.featured}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ featured: e.target.checked })}
                />
                Featured event
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ImageField
                api={api}
                value={draft.imageKey ? { key: draft.imageKey, url: draft.imageUrl } : null}
                onChange={(item) =>
                  patch(
                    item
                      ? { imageKey: item.key, imageUrl: item.url ?? null, imageAlt: draft.imageAlt || item.alt }
                      : { imageKey: null, imageUrl: null }
                  )
                }
              />
              {draft.imageKey && (
                <div className="space-y-1.5">
                  <Label htmlFor="event-image-alt">Image description (alt text)</Label>
                  <Input
                    id="event-image-alt"
                    value={draft.imageAlt}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ imageAlt: e.target.value })}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={draft.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => patch({ description: e.target.value })}
                rows={6}
                placeholder="What's happening? Blank lines start a new paragraph."
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="event-link-url">Link (optional)</Label>
                  <Input
                    id="event-link-url"
                    value={draft.linkUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ linkUrl: e.target.value })}
                    placeholder="https://…"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="event-link-label">Link label</Label>
                  <Input
                    id="event-link-label"
                    value={draft.linkLabel}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ linkLabel: e.target.value })}
                    placeholder="Book tickets"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <EventRecurrenceCard
            value={draft.recurrence}
            baseDate={draft.date}
            onChange={(recurrence) => patch({ recurrence })}
          />
          {activeModules.includes("booking") && id && (
            <EventBookingCard api={api} eventId={id} />
          )}
        </div>
      </div>
    </div>
  );
}
